import React, { createContext, useState, useContext, useEffect } from "react";
import apiClient from "../api/apiClient";
import {
  DEFAULT_HERO_CONTENT,
  firstDefinedString,
  normalizeHeroColor,
} from "../constants/heroContent";

const SettingsContext = createContext();

const SETTINGS_ID = 1;
const HERO_IMAGE_STORAGE_KEY = "mockee_hero_section_image_url";
const HERO_CONTENT_STORAGE_KEY = "mockee_hero_section_content";

export const SITE_STATUS = {
  ACTIVE: "ACTIVE",
  MAINTENANCE: "MAINTENANCE",
  DOWN: "DOWN",
};

/** Normalize API site_status; unknown/missing → ACTIVE. */
export const normalizeSiteStatus = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  if (
    normalized === SITE_STATUS.MAINTENANCE ||
    normalized === SITE_STATUS.DOWN ||
    normalized === SITE_STATUS.ACTIVE
  ) {
    return normalized;
  }
  return SITE_STATUS.ACTIVE;
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

export const getCachedHeroImageUrl = () => {
  try {
    return (localStorage.getItem(HERO_IMAGE_STORAGE_KEY) || "").trim();
  } catch {
    return "";
  }
};

export const getCachedHeroContent = () => {
  try {
    const raw = localStorage.getItem(HERO_CONTENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCachedHeroUrl = (url) => {
  try {
    const next = (url || "").trim();
    if (next) {
      localStorage.setItem(HERO_IMAGE_STORAGE_KEY, next);
    } else {
      localStorage.removeItem(HERO_IMAGE_STORAGE_KEY);
    }
  } catch {
    // Ignore private-mode / blocked storage.
  }
};

const writeCachedHeroContent = (content) => {
  try {
    localStorage.setItem(HERO_CONTENT_STORAGE_KEY, JSON.stringify(content));
  } catch {
    // Ignore private-mode / blocked storage.
  }
};

const pickHeroUrl = (row = {}) => {
  const candidates = [
    row.hero_section_image_url,
    row.heroSectionImageUrl,
    typeof row.hero_section_image === "string" ? row.hero_section_image : null,
    row.hero_section_image?.url,
    row.hero_section_image?.src,
  ];
  const url = candidates.find((value) => typeof value === "string" && value.trim());
  return url ? url.trim() : "";
};

const extractSettingsRow = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data.find(Boolean) || null;
  if (Array.isArray(data.results)) return data.results.find(Boolean) || null;
  if (
    data.id != null ||
    data.hero_section_image_url ||
    data.hero_section_image ||
    data.minimum_mockup_price != null ||
    data.site_status ||
    data.hero_headline != null ||
    data.hero_body != null
  ) {
    return data;
  }
  if (data.data && data.data !== data) return extractSettingsRow(data.data);
  return null;
};

const mapSettings = (row = {}, fallback = {}) => {
  const cached = getCachedHeroContent() || {};
  return {
    id: row.id ?? fallback.id ?? SETTINGS_ID,
    minimumPrice:
      parseFloat(row.minimum_mockup_price ?? fallback.minimumPrice) || 0,
    siteStatus: normalizeSiteStatus(row.site_status ?? fallback.siteStatus),
    heroSectionImageUrl: pickHeroUrl(row) || fallback.heroSectionImageUrl || "",
    heroHeadline: firstDefinedString(
      row.hero_headline,
      row.heroHeadline,
      fallback.heroHeadline,
      cached.headline,
      DEFAULT_HERO_CONTENT.headline
    ),
    heroBody: firstDefinedString(
      row.hero_body,
      row.heroBody,
      fallback.heroBody,
      cached.body,
      DEFAULT_HERO_CONTENT.body
    ),
    heroHeadlineColor: normalizeHeroColor(
      firstDefinedString(
        row.hero_headline_color,
        row.heroHeadlineColor,
        fallback.heroHeadlineColor,
        cached.headlineColor,
        DEFAULT_HERO_CONTENT.headlineColor
      )
    ),
    heroCtaLabel: firstDefinedString(
      row.hero_cta_label,
      row.heroCtaLabel,
      fallback.heroCtaLabel,
      cached.ctaLabel,
      DEFAULT_HERO_CONTENT.ctaLabel
    ),
    heroCtaUrl: firstDefinedString(
      row.hero_cta_url,
      row.heroCtaUrl,
      fallback.heroCtaUrl,
      cached.ctaUrl,
      DEFAULT_HERO_CONTENT.ctaUrl
    ),
  };
};

const applySettings = (row, fallback = {}) => {
  const next = mapSettings(row, fallback);
  writeCachedHeroUrl(next.heroSectionImageUrl);
  writeCachedHeroContent({
    headline: next.heroHeadline,
    body: next.heroBody,
    headlineColor: next.heroHeadlineColor,
    ctaLabel: next.heroCtaLabel,
    ctaUrl: next.heroCtaUrl,
    imageUrl: next.heroSectionImageUrl,
  });
  return next;
};

const getInitialSettings = () => {
  const cached = getCachedHeroContent() || {};
  return {
    id: SETTINGS_ID,
    minimumPrice: 0,
    siteStatus: SITE_STATUS.ACTIVE,
    heroSectionImageUrl: cached.imageUrl || getCachedHeroImageUrl(),
    heroHeadline: firstDefinedString(
      cached.headline,
      DEFAULT_HERO_CONTENT.headline
    ),
    heroBody: firstDefinedString(cached.body, DEFAULT_HERO_CONTENT.body),
    heroHeadlineColor: normalizeHeroColor(
      cached.headlineColor,
      DEFAULT_HERO_CONTENT.headlineColor
    ),
    heroCtaLabel: firstDefinedString(
      cached.ctaLabel,
      DEFAULT_HERO_CONTENT.ctaLabel
    ),
    heroCtaUrl: firstDefinedString(cached.ctaUrl, DEFAULT_HERO_CONTENT.ctaUrl),
  };
};

const HERO_FIELD_MAP = {
  heroHeadline: "hero_headline",
  heroBody: "hero_body",
  heroHeadlineColor: "hero_headline_color",
  heroCtaLabel: "hero_cta_label",
  heroCtaUrl: "hero_cta_url",
};

const applyHeroPayload = (payload, newSettings, asFormData) => {
  Object.entries(HERO_FIELD_MAP).forEach(([jsKey, apiKey]) => {
    if (newSettings[jsKey] == null) return;
    const value = String(newSettings[jsKey]);
    if (asFormData) payload.append(apiKey, value);
    else payload[apiKey] = value;
  });
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(getInitialSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const response = await apiClient.get(`/settings/${SETTINGS_ID}/`);
      if (!response.ok) return;

      const row = extractSettingsRow(response.data);
      if (row) setSettings((prev) => applySettings(row, prev));
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const updateSettings = async (newSettings) => {
    setLoading(true);
    try {
      const hasImage = newSettings.heroSectionImage instanceof File;
      let response;
      const settingsId = settings.id || SETTINGS_ID;

      if (hasImage) {
        const formData = new FormData();
        if (newSettings.minimumPrice != null) {
          formData.append("minimum_mockup_price", String(newSettings.minimumPrice));
        }
        if (newSettings.siteStatus != null) {
          formData.append(
            "site_status",
            normalizeSiteStatus(newSettings.siteStatus)
          );
        }
        applyHeroPayload(formData, newSettings, true);
        formData.append("hero_section_image", newSettings.heroSectionImage);
        response = await apiClient.patch(`/settings/${settingsId}/`, formData);
      } else {
        const payload = {};
        if (newSettings.minimumPrice != null) {
          payload.minimum_mockup_price = newSettings.minimumPrice;
        }
        if (newSettings.siteStatus != null) {
          payload.site_status = normalizeSiteStatus(newSettings.siteStatus);
        }
        applyHeroPayload(payload, newSettings, false);
        response = await apiClient.patch(`/settings/${settingsId}/`, payload);
      }

      if (!response.ok) return { success: false };

      const clientFallback = {
        ...settings,
        ...(newSettings.minimumPrice != null
          ? { minimumPrice: newSettings.minimumPrice }
          : {}),
        ...(newSettings.heroHeadline != null
          ? { heroHeadline: newSettings.heroHeadline }
          : {}),
        ...(newSettings.heroBody != null ? { heroBody: newSettings.heroBody } : {}),
        ...(newSettings.heroHeadlineColor != null
          ? { heroHeadlineColor: newSettings.heroHeadlineColor }
          : {}),
        ...(newSettings.heroCtaLabel != null
          ? { heroCtaLabel: newSettings.heroCtaLabel }
          : {}),
        ...(newSettings.heroCtaUrl != null
          ? { heroCtaUrl: newSettings.heroCtaUrl }
          : {}),
      };

      const updated = extractSettingsRow(response.data);
      if (updated) {
        setSettings((prev) => applySettings(updated, { ...prev, ...clientFallback }));
      } else {
        setSettings((prev) => applySettings({}, { ...prev, ...clientFallback }));
      }

      const refresh = await apiClient.get(`/settings/${settingsId}/`);
      if (refresh.ok) {
        const row = extractSettingsRow(refresh.data);
        if (row) {
          setSettings((prev) =>
            applySettings(row, { ...prev, ...clientFallback })
          );
        }
      }
      return { success: true };
    } catch (error) {
      console.error("Error updating settings:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    settings,
    updateSettings,
    loading,
    minimumPrice: settings?.minimumPrice ?? 0,
    siteStatus: settings?.siteStatus ?? SITE_STATUS.ACTIVE,
    heroSectionImageUrl: settings?.heroSectionImageUrl || "",
    heroHeadline: settings?.heroHeadline || DEFAULT_HERO_CONTENT.headline,
    heroBody: settings?.heroBody ?? DEFAULT_HERO_CONTENT.body,
    heroHeadlineColor:
      settings?.heroHeadlineColor || DEFAULT_HERO_CONTENT.headlineColor,
    heroCtaLabel: settings?.heroCtaLabel || "",
    heroCtaUrl: settings?.heroCtaUrl || "",
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
